import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { RouteFallback } from '@/components/common/RouteFallback';
import { pageLoaders } from '@/routes/pageLoaders';
import { NotFound } from '@/components/common/NotFound';
/** Deep import: the guard is needed on the first paint, the sign-in pages are not. */
import { RequireAuth } from '@/features/auth/components/RequireAuth';

/**
 * Shell routes render inside <AppShell> (icon rail + sidebar + content), behind
 * <RequireAuth>. The meeting route renders full-bleed, also authenticated.
 * /login and /signup are the only public paths.
 *
 * Every route below has a real page; an address that matches none of them gets
 * <NotFound>. A new page is lazy-loaded exactly like AppShell below. Do not
 * change the path strings — they are owned by `@/constants/routes`.
 */
const AppShell = lazy(() =>
  import('@/components/layout/AppShell').then((m) => ({ default: m.AppShell })),
);
const MeetingRoom = lazy(() =>
  import('@/components/meeting/MeetingRoom').then((m) => ({ default: m.MeetingRoom })),
);
/**
 * Calls. Two chunks, because the two surfaces are needed at different moments:
 * `/calls` is a list somebody browses, `/call/:callId` is a live media room that
 * has to be on screen the instant a ring is answered.
 */
const CallsPage = lazy(() => pageLoaders.calls().then((m) => ({ default: m.CallsPage })));
const CallRoomPage = lazy(() =>
  import('@/features/calls/pages/CallPage').then((m) => ({ default: m.CallPage })),
);
/** Chat is route-split: its virtualiser, pickers and mock adapter stay out of the initial bundle. */
const ChatPage = lazy(() => pageLoaders.chat().then((m) => ({ default: m.ChatPage })));
/** Teams directory and the channel workspace are each their own chunk. */
const TeamsPage = lazy(() => pageLoaders.teams().then((m) => ({ default: m.TeamsPage })));
const ChannelPage = lazy(() =>
  pageLoaders.channels().then((m) => ({ default: m.ChannelPage })),
);
/**
 * The voice room is its own chunk, but the voice FEATURE is not: the dock and
 * the sidebar rows live in the shell chunk because they must work before this
 * page is ever opened.
 */
const VoiceRoomPage = lazy(() =>
  import('@/features/voice/VoiceRoomPage').then((m) => ({ default: m.VoiceRoomPage })),
);
/**
 * Deep import on purpose: `@/features/notifications` is already in the initial
 * chunk (AppProviders mounts AppOverlays), so importing the barrel here would
 * drag the settings panel and its forms into it.
 */
const ActivityPage = lazy(() =>
  pageLoaders.activity().then((m) => ({ default: m.ActivityPage })),
);
/**
 * Search is its own chunk: the facet rail, the result virtualiser and the five
 * result cards are only needed once someone actually searches. The command
 * palette's "search everywhere" row navigates here, which prefetches it.
 */
const SearchPage = lazy(() => import('@/features/search').then((m) => ({ default: m.SearchPage })));
/**
 * The workspace Drive. Its own chunk: the preview dialog and the browse list are
 * only needed by somebody who went looking for a file, not on first paint.
 */
const FilesPage = lazy(() => pageLoaders.files().then((m) => ({ default: m.FilesPage })));
/**
 * Tasks. Its own chunk: the lists, the task panel and the form are needed by
 * somebody who opened Tasks (or a channel's Tasks tab), not on first paint.
 */
const TasksPage = lazy(() => pageLoaders.tasks().then((m) => ({ default: m.TasksPage })));
/**
 * The integration surface. Its own chunk: six panels, their modals and the
 * delivery log are only needed by somebody actively wiring this workspace into
 * another system, which is a minority of a minority of sessions.
 */
const AppsPage = lazy(() => import('@/features/apps').then((m) => ({ default: m.AppsPage })));
/**
 * The admin console is one lazy chunk of its own. Most people never open it, so
 * its tables, policy editor and CSV importer must never sit in the main bundle.
 */
const AdminLayout = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminLayout })),
);
const AdminOverviewPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminOverviewPage })),
);
const AdminUsersPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminUsersPage })),
);
const AdminTeamsPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminTeamsPage })),
);
const AdminGroupsPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminGroupsPage })),
);
const AdminPoliciesPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminPoliciesPage })),
);
const AdminOrgPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminOrgPage })),
);
/** Security & compliance: policy, audit viewer, retention/hold/export. */
const AdminSecurityPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminSecurityPage })),
);
/** Telephony: trunks, numbers, queues, auto-attendants, call records. */
const AdminPstnPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminPstnPage })),
);
const AdminAuditPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminAuditPage })),
);
const AdminCompliancePage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminCompliancePage })),
);
/** Federation + provisioning: OIDC/SAML providers, SCIM tokens, the trail. */
const AdminIdentityPage = lazy(() =>
  import('@/features/admin').then((m) => ({ default: m.AdminIdentityPage })),
);
/**
 * The workspace recordings library. Its own chunk: it pulls in the multi-part
 * player and the list rows, which nobody who never opens a recording should
 * have to download.
 */
const RecordingsPage = lazy(() =>
  pageLoaders.recordings().then((m) => ({ default: m.RecordingsPage })),
);
/**
 * The calendar. Its own chunk: the grid, the scheduling form and the directory
 * type-ahead are only needed by somebody who opened it, and nothing on the
 * first paint imports any of them.
 */
const CalendarPage = lazy(() =>
  pageLoaders.calendar().then((m) => ({ default: m.CalendarPage })),
);
/**
 * Self-service security. Its own chunk rather than part of the auth chunk: the
 * QR encoder it pulls in is only ever needed by someone actively enrolling MFA.
 */
const SecurityPage = lazy(() =>
  import('@/features/auth/pages/SecurityPage').then((m) => ({ default: m.SecurityPage })),
);
const SettingsPage = lazy(() => pageLoaders.settings().then((m) => ({ default: m.SettingsPage })));
/** Auth pages are their own chunk: signed-in users never download the forms. */
const LoginPage = lazy(() =>
  import('@/features/auth/pages/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import('@/features/auth/pages/RegisterPage').then((m) => ({ default: m.RegisterPage })),
);
/**
 * The invitation landing page. Its own chunk AND its own public route: the
 * recipient is, by definition, somebody this workspace has never seen, and the
 * invite token is the only credential they carry. It dispatches between the two
 * kinds of invitation on the token's own shape — see InviteLandingPage.
 */
const InviteLandingPage = lazy(() =>
  import('@/features/invites').then((m) => ({ default: m.InviteLandingPage })),
);
/**
 * A shared file. Its own PUBLIC chunk for the same reason as the invitation
 * page: the person opening the link has no account here and must not download
 * the whole product to read one document.
 */
const SharedFilePage = lazy(() =>
  import('@/features/files/share').then((m) => ({ default: m.SharedFilePage })),
);
/** Console → External access: connected orgs, guests, the org-wide switch. */
const AdminExternalPage = lazy(() =>
  import('@/features/channels/external').then((m) => ({ default: m.ExternalAdminPage })),
);

/**
 * Webinars. The organiser console is one lazy chunk; the two PUBLIC pages are
 * their own, because the person opening a registration link has no account here
 * and must not download the whole product to read a headline.
 */
const WebinarsPage = lazy(() =>
  import('@/features/webinars').then((m) => ({ default: m.WebinarsPage })),
);
const WebinarDetailPage = lazy(() =>
  import('@/features/webinars').then((m) => ({ default: m.WebinarDetailPage })),
);
const GreenRoomPage = lazy(() =>
  import('@/features/webinars').then((m) => ({ default: m.GreenRoomPage })),
);
const WebinarRegisterPage = lazy(() =>
  import('@/features/webinars/pages/WebinarRegisterPage').then((m) => ({
    default: m.WebinarRegisterPage,
  })),
);
const JoinByCodePage = lazy(() =>
  import('@/features/calendar/pages/JoinByCodePage').then((m) => ({
    default: m.JoinByCodePage,
  })),
);
const WebinarStagePage = lazy(() =>
  import('@/features/webinars/pages/WebinarStagePage').then((m) => ({
    default: m.WebinarStagePage,
  })),
);

export function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path={ROUTES.login} element={<LoginPage />} />
        <Route path={ROUTES.register} element={<RegisterPage />} />
        <Route path={ROUTES.invitePattern} element={<InviteLandingPage />} />
        {/* A share link. Public by definition — the token in the path is the
            entire claim, and the recipient is usually outside the workspace. */}
        <Route path={ROUTES.sharePattern} element={<SharedFilePage />} />
        {/* Public webinar surfaces. Outside <RequireAuth> on purpose: the
            registrant has no account in this workspace and never will — their
            emailed token is the only credential involved. */}
        <Route path={ROUTES.webinarPublicPattern} element={<WebinarRegisterPage />} />
        <Route path={ROUTES.webinarStagePattern} element={<WebinarStagePage />} />

        <Route element={<RequireAuth />}>
          <Route path={ROUTES.root} element={<AppShell />}>
            {/* CHAT IS THE HOME. Opening the app dropped people on the Teams
                directory — a list of teams to browse — when what almost
                everybody wants first is the conversation waiting for them.
                Teams' own client opens on Chat for the same reason. */}
            <Route index element={<Navigate to={ROUTES.chat} replace />} />
            <Route path="activity" element={<ActivityPage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="chat/:conversationId" element={<ChatPage />} />
            <Route path="teams" element={<TeamsPage />} />
            {/* Ranked above `:channelId` by the router's static-segment rule, so
                a voice room never resolves to the text channel workspace. */}
            <Route path="teams/:teamId/voice/:channelId" element={<VoiceRoomPage />} />
            <Route path="teams/:teamId/:channelId" element={<ChannelPage />} />
            <Route path="search" element={<SearchPage />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="profile" element={<Navigate to={`${ROUTES.settings}?section=profile`} replace />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            {/* Redeeming a join code. INSIDE the shell — it is a doorway, not
                a meeting, and somebody who followed a dead link needs a way
                back to their work. Both forms, so a pasted link and the
                toolbar's "Join with a code" reach one surface. */}
            <Route path="join" element={<JoinByCodePage />} />
            <Route path="join/:joinCode" element={<JoinByCodePage />} />
            <Route path="calls" element={<CallsPage />} />
            <Route path="recordings" element={<RecordingsPage />} />
            <Route path="files" element={<FilesPage />} />
            {/* Both forms reach one page: the second has a task open. A
                notification about a task links to the second. */}
            <Route path="tasks" element={<TasksPage />} />
            <Route path="tasks/:taskId" element={<TasksPage />} />
            <Route path="apps" element={<AppsPage />} />
            <Route path="webinars" element={<WebinarsPage />} />
            {/* Ranked above `:webinarId` by the router's static-segment rule,
                so the green room never resolves to the console. */}
            <Route path="webinars/:webinarId/green-room" element={<GreenRoomPage />} />
            <Route path="webinars/:webinarId" element={<WebinarDetailPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path={ROUTES.meetingPattern} element={<MeetingRoom />} />
          {/* A live call, full-bleed like a meeting: answering a ring lands here. */}
          <Route path={ROUTES.callPattern} element={<CallRoomPage />} />

          {/* Console. <AdminLayout> re-gates on the admin role — <RequireAuth>
              only proves you are signed in, not that you may administer. */}
          <Route path={ROUTES.admin} element={<AdminLayout />}>
            <Route index element={<AdminOverviewPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="teams" element={<AdminTeamsPage />} />
            <Route path="groups" element={<AdminGroupsPage />} />
            <Route path="policies" element={<AdminPoliciesPage />} />
            <Route path="security" element={<AdminSecurityPage />} />
            <Route path="identity" element={<AdminIdentityPage />} />
            <Route path="external" element={<AdminExternalPage />} />
            <Route path="telephony" element={<AdminPstnPage />} />
            <Route path="audit" element={<AdminAuditPage />} />
            <Route path="compliance" element={<AdminCompliancePage />} />
            <Route path="org" element={<AdminOrgPage />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
